import React from 'react';
import { PermissionType } from '../../store/pageFrame/types';
import { useGetStripeSubscription, useGetMemberships } from '../accounts-api';
import { useLocalStorage } from '../use-storage';

type UseTrialBannerArgs = {
  trialEndDate?: string;
  hasDs3Access?: boolean;
  isPendingAccessCheck: boolean;
};

const ONE_DAY_IN_MILLIS = 24 * 60 * 60 * 1000;

export function useTrialBanner(args: UseTrialBannerArgs) {
  const { trialEndDate, hasDs3Access, isPendingAccessCheck } = args;

  const { data: stripe } = useGetStripeSubscription();
  const { data: memberships } = useGetMemberships();
  const isActiveCancelledSubscription = stripe?.status === 'active' && !!stripe?.cancelAtPeriodEnd;

  const daysLeftInTrial = React.useMemo(() => {
    if (!trialEndDate) return Number.POSITIVE_INFINITY;
    return Math.floor((new Date(trialEndDate).getTime() - Date.now()) / ONE_DAY_IN_MILLIS);
  }, [trialEndDate]);

  const daysLeftInSubscription = React.useMemo(() => {
    if (!stripe?.currentPeriodEnd) return 0;
    return Math.floor((new Date(stripe.currentPeriodEnd).getTime() - Date.now()) / ONE_DAY_IN_MILLIS);
  }, [stripe?.currentPeriodEnd]);

  const trialBannerDismissal = useBannerDismissal({ key: 'banner.trial.dismissed', expiresAfterDays: 1 });

  const cancelledBannerDismissal = useBannerDismissal({
    key: 'banner.cancelled.dismissed',
    expiresAfterDays: isActiveCancelledSubscription && daysLeftInSubscription > 30 ? 7 : 1,
  });

  // Check if user has DS3 access through non-Stripe subscriptions (enterprise, group, etc.)
  const hasNonStripeDs3Access =
    memberships?.subscriptions
      ?.filter((s) => s?.source !== 'Forms/DS3 Trial' && s?.source !== 'Individual' && !s?.source?.includes('Stripe'))
      ?.some((s) => s?.permissions?.includes(PermissionType.DS3)) || false;

  const showCancelledBanner =
    !cancelledBannerDismissal.isDismissed &&
    !isPendingAccessCheck &&
    !hasNonStripeDs3Access &&
    (stripe?.status === 'canceled' || isActiveCancelledSubscription);

  const hasOrHadAStripeSubscription = !!stripe?.status;
  const showTrialBanner =
    !showCancelledBanner &&
    !trialBannerDismissal.isDismissed &&
    !isPendingAccessCheck &&
    trialEndDate &&
    !hasDs3Access &&
    !hasOrHadAStripeSubscription;

  return {
    // Trial banner
    showTrialBanner: !!showTrialBanner,
    dismissTrialBanner: () => trialBannerDismissal.dismiss(),
    daysLeft: daysLeftInTrial,
    // Cancelled subscription banner
    showCancelledBanner,
    dismissCancelledBanner: () => cancelledBannerDismissal.dismiss(),
    isActiveCancelledSubscription,
  };
}

function useBannerDismissal(args: { key: string; expiresAfterDays: number }) {
  const { key, expiresAfterDays } = args;
  const expiresAfterInMillis = expiresAfterDays * ONE_DAY_IN_MILLIS;

  const [dismissedAt, setDismissedAt] = useLocalStorage<number>(key);
  const isDismissalExpired = dismissedAt && dismissedAt + expiresAfterInMillis < Date.now();

  // Clear storage entry after expiration
  React.useEffect(() => {
    if (isDismissalExpired) setDismissedAt(null);
  }, [isDismissalExpired, setDismissedAt]);

  function dismiss() {
    setDismissedAt(Date.now());
  }

  return {
    isDismissed: !!dismissedAt && !isDismissalExpired,
    dismiss,
  };
}
